import Form from '@components/CustomForm/Form';
import {NEW_PAYMENT} from '@constants/paymentType';
import {MONTHLY} from '@constants/period';
import {useCurrentUserContext} from '@context/CurrentUserContext';
import PaymentError from '@errors/PaymentError';
import type {CustomFormProps, FormValues} from '@interfaces/CustomForm';
import Analytics from '@services/analytics/Analytics';
import {getCurrentLanguage, localize} from '@services/i18n';
import {isApiError, isPaymentError} from '@utils/errorUtils';
import type {FormikBag} from 'formik';
import isObject from 'lodash.isobject';
import type {ReactElement} from 'react';
import {getPeriodMonthNum} from '@utils/subscriptionUtils';
import {PaymentGates} from '@constants/paymentInfo';
import {getBuilder} from '@services/payment/PopupFactory';
import {ProductType} from '@constants/productType';

interface Props extends CustomFormProps {
    onAfterSuccessfulPayment(): void;
}

export default function FormScreen(props: Props): ReactElement<Props> {
    const currentState = useCurrentUserContext();

    async function handleSubmit(values: FormValues, formikBag: FormikBag<object, FormValues>) {
        formikBag.setStatus(undefined);
        const description = localize('customPayment.paymentDescription.purchase');

        try {
            const builder = getBuilder(
                PaymentGates.CloudPayments,
                ProductType.Payment,
                values.email,
                description,
            )
                .setItem(description, values.sum)
                .setCustomData({
                    ...values,
                    title: props.title,
                    type: NEW_PAYMENT,
                    purpose: props.purpose,
                    language: getCurrentLanguage(),
                })
                .setCompleteHandler(() => {
                    if (values.isRecurrent) {
                        Analytics.triggerEventWithPayment({
                            eventName: 'Успешная подписка с кастомной формы',
                            eventType: 'subscribe',
                            eventLabel: 'custom',
                            price: values.sum,
                            currency: 'RUB',
                        });
                    } else {
                        Analytics.triggerEventWithPayment({
                            eventName: 'Успешная оплата с кастомной формы',
                            eventType: 'payment',
                            eventLabel: 'custom',
                            price: values.sum,
                            currency: 'RUB',
                        });
                    }
                });

            if (values.isRecurrent) {
                builder.setRecurrentData({
                    monthNum: getPeriodMonthNum(MONTHLY),
                });
            }

            await builder
                .buildCharge()
                .then(props.onAfterSuccessfulPayment)
                // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
                .catch((reason: string) => {
                    throw new PaymentError(reason);
                });
        } catch (error) {
            if (
                isApiError(error) &&
                isObject(error.payload) &&
                Object.keys(error.payload).length > 0
            ) {
                formikBag.setErrors(error.payload);
            } else if (!isPaymentError(error)) {
                formikBag.setStatus(localize('Error'));
            }
        }

        formikBag.setSubmitting(false);
    }

    return (
        <Form
            title={props.title}
            subtitle={props.subtitle}
            withEditableSum={props.withEditableSum ?? false}
            withRecurrentSwitch={props.withRecurrentSwitch ?? false}
            buttonLabel={props.buttonLabel}
            userAgreementLink={props.userAgreementLink}
            currency="rub"
            initialValues={{
                minSum: props.minSum,
                sum: props.defaultSum ?? 0,
                email: currentState.email ?? '',
                isRecurrent: props.withRecurrentSwitch ?? false,
            }}
            onSubmit={handleSubmit}
        />
    );
}
